import { BoxV2 as Box, Button, Flex, TextInput } from 'portal-commons';
import { FunctionComponent, HTMLAttributes, useEffect } from 'react';
import { useController, useForm } from 'react-hook-form';
import { faCopy, faKey } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { ApiAccount } from '../types';
import {
  createMaxLengthValidator,
  createRequiredValidator,
  isValidInput,
} from '../validators';
import { Modal } from '@/components';
import { toastFlashMessage } from '@/utils';
import { globalPutService } from '@/utils/globalApiServices';

interface EditApiKeyModalProps {
  open?: boolean;
  account: ApiAccount;
  onCancel?: () => void;
  onSave?: (account: ApiAccount) => void;
}

interface FormValues {
  label: string;
}

const Title: FunctionComponent = ({ children }) => (
  <Flex
    sx={{
      flexDirection: 'row',
      alignItems: 'center',
      gap: 's',
      padding: '12px 0',
      color: 't.black80',
      fontWeight: 600,
      fontSize: 'H600',
      lineHeight: 'normal',
    }}
  >
    {children}
  </Flex>
);

const Fields: FunctionComponent = ({ children }) => (
  <Flex
    sx={{
      flexDirection: 'column',
      gap: 'l',
      width: '100%',
    }}
  >
    {children}
  </Flex>
);

const FieldWrapper: FunctionComponent<HTMLAttributes<HTMLDivElement>> = ({
  children,
  ...props
}) => (
  <Box sx={{ position: 'relative', width: '100%' }} {...props}>
    {children}
  </Box>
);

const CopyButton: FunctionComponent<HTMLAttributes<HTMLDivElement>> = (
  props
) => (
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      right: '12px',
      color: 't.black70',
      fontWeight: 400,
      fontSize: 'H300',
      cursor: 'pointer',
      transform: 'translateY(-50%)',
    }}
    {...props}
  >
    <FontAwesomeIcon icon={faCopy} />
  </Box>
);

const Actions: FunctionComponent = ({ children }) => (
  <Flex
    sx={{
      flexDirection: 'row',
      alignItems: 'center',
      gap: 'l',
      padding: '30px 0 40px',
    }}
  >
    {children}
  </Flex>
);

const EditApiKeyModal: FunctionComponent<EditApiKeyModalProps> = ({
  open,
  account,
  onCancel,
  onSave,
}) => {
  const {
    control,
    formState: { errors, isSubmitting },
    reset,
    handleSubmit,
  } = useForm<FormValues>({
    values: {
      label: account.displayName,
    },
  });
  const {
    field: { value: label, onChange: setLabel, onBlur: validateLabel },
    fieldState: { error: labelError },
  } = useController({
    control,
    name: 'label',
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please enter Label'),
        isValidInput,
        withValidMaxLength: createMaxLengthValidator(20),
      },
    },
  });

  const handleApiKeyCopy = () => {
    navigator.clipboard.writeText(account.apiKey);
    toastFlashMessage('API Key has been copied to your clipboard', 'success');
  };

  const handleValid = async () => {
    const response = await globalPutService(`mno/api/account/${account.id}`, {
      displayName: label.trim(),
    });
    if (response?.ok) {
      const account = response.data as ApiAccount;
      onSave?.(account);
      toastFlashMessage('API Key updated successfully', 'success');
    }
  };

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open]);

  useEffect(() => {
    setLabel(account.displayName);
  }, [account]);

  return (
    <Modal open={open}>
      <form
        style={{ display: 'contents' }}
        onSubmit={handleSubmit(handleValid)}
      >
        <Title>
          <Box as="span" sx={{ fontWeight: 400 }}>
            <FontAwesomeIcon icon={faKey} />
          </Box>
          <Box as="span">Edit API Key</Box>
        </Title>
        <Fields>
          <FieldWrapper data-testid="editApiKeyModalLabel">
            <TextInput
              error={!!labelError?.message}
              label="API Label"
              note={labelError?.message}
              value={label}
              onChange={setLabel}
              onBlur={validateLabel}
            />
          </FieldWrapper>
          <FieldWrapper data-testid="editApiKeyModalApiKey">
            <TextInput readOnly label="API Key" value={account.apiKey} />
            <CopyButton onClick={handleApiKeyCopy} />
          </FieldWrapper>
        </Fields>
        <Actions>
          <Button
            variant="outline"
            onClick={onCancel}
            data-testid="editApiKeyModalCancelButton"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={
              isSubmitting ||
              Object.values(errors).some((error) => !!error?.message)
            }
            data-testid="editApiKeyModalSaveButton"
          >
            Save
          </Button>
        </Actions>
      </form>
    </Modal>
  );
};

export default EditApiKeyModal;
